import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useConnectPlaneProject } from './hooks/useProjects'
import { Loader2, FolderKanban } from 'lucide-react'

interface CreateProjectModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateProjectModal({ open, onOpenChange }: CreateProjectModalProps) {
  const [planeProjectRef, setPlaneProjectRef] = useState('')
  const projectRefInputRef = useRef<HTMLInputElement>(null)

  const connectPlaneProject = useConnectPlaneProject()
  const isLoading = connectPlaneProject.isPending

  // Focus name input when modal opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure the dialog is mounted
      setTimeout(() => {
        projectRefInputRef.current?.focus()
      }, 100)
    }
  }, [open])

  const resetForm = () => {
    setPlaneProjectRef('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!planeProjectRef.trim()) return

    try {
      await connectPlaneProject.mutateAsync({
        planeProjectRef: planeProjectRef.trim(),
      })
      toast.success('Plane project connected successfully')
      resetForm()
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to connect Plane project', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Connect Plane Project</DialogTitle>
          <DialogDescription>
            Create a DSP mirror from an existing Plane project. DSP uses Plane metadata for
            channel and Drive context, and keeps a local mirror record for search only.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/50 p-3" role="region" aria-label="Connection mode">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary">
                <FolderKanban className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Plane-first connection</p>
                <p>DSP creates a mirror record only and does not override Plane channel or Drive metadata.</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plane-project-ref">Plane Project ID or Identifier *</Label>
            <Input
              ref={projectRefInputRef}
              id="plane-project-ref"
              value={planeProjectRef}
              onChange={(e) => setPlaneProjectRef(e.target.value)}
              placeholder="bf8509ad-... or LGHS25"
              required
              className="min-h-[44px]"
            />
            <p className="text-xs text-muted-foreground">
              Existing Plane project UUID 또는 identifier를 입력합니다.
            </p>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="min-h-[44px] w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !planeProjectRef.trim()}
              className="min-h-[44px] w-full sm:w-auto"
              aria-busy={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              Connect Plane Project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
